/*
 * theme.js — global behavior (vanilla, no dependencies, loaded with defer)
 * - Dialogs (drawers, modals) on top of native <dialog>
 * - AJAX cart (Cart API + Section Rendering API) and cart drawer
 * - Header (sticky, dropdowns), announcement rotator
 * - Predictive search, facets (filters/sort), async recommendations
 * - Consent helper for non-essential scripts
 */
(() => {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const strings = theme.strings || {};
  const routes = theme.routes || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Utilities ------------------------------------------------------------ */
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const formatMoney = (cents, format = theme.moneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) / 100;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const [int, dec] = n.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return fmt(value, 0);
        case 'amount_with_comma_separator':
          return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return fmt(value, 2, ' ', ',');
        default:
          return fmt(value, 2);
      }
    });
  };

  let toastTimer;
  const toast = (message, type = 'info') => {
    const el = document.getElementById('Toast');
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('toast--error', type === 'error');
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
  };

  Object.assign(theme, { debounce, parseHTML, formatMoney, toast });

  /* Dialogs --------------------------------------------------------------- */
  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    if (opener) opener.setAttribute('aria-expanded', 'true');
    dialog.dispatchEvent(new CustomEvent('dialog:open'));
  };

  const closeDialog = (dialog) => {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    const finish = () => {
      dialog.classList.remove('is-closing');
      dialog.close();
      if (!document.querySelector('dialog[open]')) document.documentElement.style.overflow = '';
      const opener = dialog._opener;
      if (opener) {
        opener.setAttribute('aria-expanded', 'false');
        if (document.contains(opener)) opener.focus({ preventScroll: true });
      }
      dialog.dispatchEvent(new CustomEvent('dialog:close'));
    };
    if (reducedMotion || !dialog.classList.contains('drawer')) return finish();
    dialog.classList.add('is-closing');
    dialog.addEventListener('animationend', finish, { once: true });
    setTimeout(() => dialog.classList.contains('is-closing') && finish(), 400);
  };

  theme.openDialog = openDialog;
  theme.closeDialog = closeDialog;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.dataset.modalOpen), opener);
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    // Click on the backdrop: the event target is the <dialog> itself.
    if (event.target.tagName === 'DIALOG' && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside || event.target.classList.contains('zoom-dialog')) closeDialog(event.target);
    }
  });

  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target.tagName === 'DIALOG') {
        event.preventDefault();
        closeDialog(event.target);
      }
    },
    true
  );

  /* Cart API -------------------------------------------------------------- */
  const cartSections = () => {
    const ids = [];
    if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
    ids.push('cart-icon-bubble');
    const main = document.querySelector('[data-main-cart]');
    if (main) ids.push(main.dataset.section);
    return ids;
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      if (id === 'cart-drawer') {
        const target = document.getElementById('CartDrawer-Inner');
        const source = doc.getElementById('CartDrawer-Inner');
        if (target && source) target.innerHTML = source.innerHTML;
      } else if (id === 'cart-icon-bubble') {
        const target = document.getElementById('cart-icon-bubble');
        const source = doc.querySelector('.shopify-section');
        if (target && source) {
          target.innerHTML = source.innerHTML;
          const count = target.querySelector('.cart-count');
          if (count && !reducedMotion) count.classList.add('is-bumped');
          document.querySelectorAll('[data-cart-count-mirror]').forEach((mirror) => {
            mirror.innerHTML = count ? count.outerHTML : '';
          });
        }
      } else {
        const target = document.getElementById('MainCart');
        const source = doc.getElementById('MainCart');
        if (target && source) target.innerHTML = source.innerHTML;
      }
    });
  };

  const cartRequest = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body instanceof FormData ? body : JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || strings.cartError);
      error.data = data;
      throw error;
    }
    return data;
  };

  const CartAPI = {
    // `extraIds`: variant ids added together with the main item (e.g. gift packaging).
    async add(formData, extraIds = []) {
      let body = formData;
      if (extraIds.length) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value) properties[match[1]] = value;
        }
        const main = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity')) || 1, properties };
        if (formData.get('selling_plan')) main.selling_plan = formData.get('selling_plan');
        body = {
          items: [main, ...extraIds.map((id) => ({ id: Number(id), quantity: 1 }))],
          sections: cartSections(),
          sections_url: window.location.pathname
        };
      } else {
        formData.append('sections', cartSections().join(','));
        formData.append('sections_url', window.location.pathname);
      }
      const data = await cartRequest(`${routes.cartAdd}.js`, body);
      renderCartSections(data.sections);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      return data;
    },
    async change(line, quantity) {
      const data = await cartRequest(`${routes.cartChange}.js`, {
        line,
        quantity,
        sections: cartSections(),
        sections_url: window.location.pathname
      });
      renderCartSections(data.sections);
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: data }));
      return data;
    },
    async updateNote(note) {
      return cartRequest(`${routes.cartUpdate}.js`, { note });
    }
  };
  theme.cart = CartAPI;

  const openCartDrawer = () => {
    const drawer = document.getElementById('CartDrawer');
    if (drawer) openDialog(drawer, document.getElementById('cart-icon'));
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cart-drawer-toggle]');
    if (toggle && document.getElementById('CartDrawer')) {
      event.preventDefault();
      openCartDrawer();
    }
  });

  /* Add to cart (all product forms, including quick add and upsells) ----- */
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form.matches('form[data-type="add-to-cart-form"]')) return;
    event.preventDefault();

    const buttons = [
      ...form.querySelectorAll('[type="submit"]'),
      ...(form.id ? document.querySelectorAll(`[form="${form.id}"][type="submit"]`) : [])
    ];
    if (buttons.some((b) => b.disabled || b.classList.contains('is-loading'))) return;

    const errorBox = form.closest('product-form')?.querySelector('[data-form-error]');
    if (errorBox) errorBox.hidden = true;
    buttons.forEach((b) => {
      b.classList.add('is-loading');
      b.setAttribute('aria-busy', 'true');
    });

    try {
      const addons = [...(form.closest('[data-product-section]')?.querySelectorAll('[data-addon]:checked') || [])];
      await CartAPI.add(new FormData(form), addons.map((input) => input.value));
      addons.forEach((input) => (input.checked = false));
      if (document.getElementById('CartDrawer')) {
        const openDialogEl = document.querySelector('dialog[open]:not(#CartDrawer)');
        if (openDialogEl) closeDialog(openDialogEl);
        openCartDrawer();
      } else {
        toast(strings.added);
      }
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = error.message;
        errorBox.hidden = false;
      } else {
        toast(error.message, 'error');
      }
    } finally {
      buttons.forEach((b) => {
        b.classList.remove('is-loading');
        b.removeAttribute('aria-busy');
      });
    }
  });

  /* Cart line edits (drawer + cart page) ----------------------------------- */
  const changeLine = async (line, quantity, item) => {
    item?.classList.add('is-loading');
    try {
      await CartAPI.change(line, quantity);
    } catch (error) {
      toast(error.message || strings.cartError, 'error');
      item?.classList.remove('is-loading');
    }
  };

  const onQuantityChange = debounce((input) => {
    const line = Number(input.dataset.line);
    const quantity = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(line, quantity, input.closest('.cart-item'));
  }, 350);

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input.matches('.quantity__input[data-line]')) onQuantityChange(input);
    if (input.matches('[data-autosubmit]')) input.form?.submit();
    if (input.matches('[data-addon-variant]')) {
      const addon = input.closest('.addon');
      const check = addon?.querySelector('[data-addon]');
      if (check) {
        check.value = input.value;
        check.checked = true;
      }
      const price = addon?.querySelector('.addon__price');
      if (price) price.textContent = `+${input.selectedOptions[0].dataset.price}`;
    }
  });

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-remove-line]');
    if (remove) {
      event.preventDefault();
      changeLine(Number(remove.dataset.removeLine), 0, remove.closest('.cart-item'));
    }
  });

  const saveNote = debounce((note) => CartAPI.updateNote(note).catch(() => {}), 500);
  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-cart-note]')) saveNote(event.target.value);
  });

  /* Custom elements ------------------------------------------------------------ */
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  define(
    'quantity-input',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.addEventListener('click', (event) => {
          const btn = event.target.closest('button');
          if (!btn) return;
          event.preventDefault();
          const step = Number(this.input.step) || 1;
          const min = Number(this.input.min) || 0;
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const current = parseInt(this.input.value, 10) || 0;
          const next = btn.name === 'plus' ? Math.min(max, current + step) : Math.max(min, current - step);
          if (next === current) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    }
  );

  define(
    'cart-drawer',
    class extends HTMLElement {
      connectedCallback() {
        // Keep drawer content fresh when the page is restored from bfcache.
        window.addEventListener('pageshow', (event) => {
          if (!event.persisted) return;
          fetch(`${routes.root}?sections=cart-drawer,cart-icon-bubble`)
            .then((r) => r.json())
            .then(renderCartSections)
            .catch(() => {});
        });
      }
    }
  );

  define(
    'announcement-rotator',
    class extends HTMLElement {
      connectedCallback() {
        this.messages = [...this.querySelectorAll('.announcement__message')];
        if (this.messages.length < 2 || reducedMotion) return;
        this.index = 0;
        const interval = Number(this.dataset.interval) || 5000;
        this.timer = setInterval(() => this.next(), interval);
        this.addEventListener('mouseenter', () => clearInterval(this.timer));
        this.addEventListener('mouseleave', () => {
          clearInterval(this.timer);
          this.timer = setInterval(() => this.next(), interval);
        });
      }
      disconnectedCallback() {
        clearInterval(this.timer);
      }
      next() {
        this.messages[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.messages.length;
        this.messages[this.index].classList.add('is-active');
      }
    }
  );

  define(
    'sticky-header',
    class extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.section-header');
        this.mode = this.dataset.sticky;
        this.lastY = window.scrollY;
        const setHeight = () => {
          this.headerHeight = this.offsetHeight;
          document.documentElement.style.setProperty('--header-offset', `${this.headerHeight}px`);
        };
        setHeight();
        new ResizeObserver(setHeight).observe(this);
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.initDropdowns();
      }
      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
      }
      onScroll() {
        const y = window.scrollY;
        this.classList.toggle('is-scrolled', y > 10);
        if (this.mode === 'scroll-up' && this.section) {
          const hide = y > this.lastY && y > (this.headerHeight || 0) * 2;
          this.section.classList.toggle('is-hidden', hide && !document.querySelector('dialog[open]'));
        }
        this.lastY = y;
      }
      initDropdowns() {
        const dropdowns = [...this.querySelectorAll('[data-hover-dropdown]')];
        const hover = window.matchMedia('(hover: hover)').matches;
        dropdowns.forEach((d) => {
          d.addEventListener('toggle', () => {
            if (d.open) dropdowns.forEach((o) => o !== d && o.removeAttribute('open'));
          });
          if (hover) {
            let timer;
            d.addEventListener('mouseenter', () => {
              clearTimeout(timer);
              d.setAttribute('open', '');
            });
            d.addEventListener('mouseleave', () => {
              timer = setTimeout(() => d.removeAttribute('open'), 150);
            });
          }
          d.addEventListener('keyup', (e) => {
            if (e.key === 'Escape') {
              d.removeAttribute('open');
              d.querySelector('summary').focus();
            }
          });
        });
        document.addEventListener('click', (e) => {
          dropdowns.forEach((d) => !d.contains(e.target) && d.removeAttribute('open'));
        });
      }
    }
  );

  define(
    'predictive-search',
    class extends HTMLElement {
      connectedCallback() {
        this.enabled = this.dataset.enabled === 'true';
        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector('[data-results]');
        this.cache = new Map();
        const dialog = this.closest('dialog');
        dialog?.addEventListener('dialog:open', () => setTimeout(() => this.input.focus(), 50));
        if (!this.enabled) return;
        this.input.addEventListener(
          'input',
          debounce(() => this.search(this.input.value.trim()), 250)
        );
        this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      }
      async search(term) {
        if (!term) {
          this.results.innerHTML = '';
          this.input.setAttribute('aria-expanded', 'false');
          return;
        }
        const key = term.toLowerCase();
        if (this.cache.has(key)) return this.render(this.cache.get(key));
        this.controller?.abort();
        this.controller = new AbortController();
        try {
          const params = new URLSearchParams({
            q: term,
            section_id: 'predictive-search',
            'resources[type]': 'product,collection,page,query',
            'resources[limit]': '6',
            'resources[options][unavailable_products]': 'last'
          });
          const response = await fetch(`${routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
          if (!response.ok) throw new Error(response.status);
          const html = await response.text();
          const markup = parseHTML(html).querySelector('[data-predictive-results]')?.outerHTML || '';
          this.cache.set(key, markup);
          this.render(markup);
        } catch (error) {
          if (error.name !== 'AbortError') this.results.innerHTML = '';
        }
      }
      render(markup) {
        this.results.innerHTML = markup;
        this.input.setAttribute('aria-expanded', markup ? 'true' : 'false');
        this.activeIndex = -1;
      }
      onKeydown(e) {
        const options = [...this.results.querySelectorAll('[role="option"]')];
        if (!options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
        if (e.key === 'Enter') {
          const active = options[this.activeIndex];
          if (active) {
            e.preventDefault();
            active.querySelector('a').click();
          }
          return;
        }
        e.preventDefault();
        this.activeIndex = e.key === 'ArrowDown' ? (this.activeIndex + 1) % options.length : (this.activeIndex - 1 + options.length) % options.length;
        options.forEach((o, i) => o.setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false'));
        this.input.setAttribute('aria-activedescendant', options[this.activeIndex].id);
      }
    }
  );

  /* Facets: filters, sorting and pagination without reload ------------------ */
  define(
    'facet-filters',
    class extends HTMLElement {
      connectedCallback() {
        this.sectionEl = this.closest('[data-collection-section]');
        this.sectionId = this.dataset.section;
        this.form = this.querySelector('[data-facets-form]');
        this.form?.addEventListener('submit', (e) => {
          e.preventDefault();
          closeDialog(this.form.closest('dialog'));
          this.apply(new URLSearchParams(new FormData(this.form)).toString());
        });
        this.querySelector('[data-sort]')?.addEventListener('change', (e) => {
          if (this.form) {
            this.apply(new URLSearchParams(new FormData(this.form)).toString());
          } else {
            const params = new URLSearchParams(window.location.search);
            params.set('sort_by', e.target.value);
            params.delete('page');
            this.apply(params.toString());
          }
        });
        this.sectionEl?.addEventListener('click', this.onLinkClick);
      }
      disconnectedCallback() {
        this.sectionEl?.removeEventListener('click', this.onLinkClick);
      }
      onLinkClick = (e) => {
        const link = e.target.closest('a[data-facet-link]');
        if (!link || e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        closeDialog(link.closest('dialog'));
        const url = new URL(link.href, window.location.origin);
        this.apply(url.searchParams.toString(), link.closest('.pagination') !== null);
      };
      async apply(query, scrollTop = false) {
        const params = new URLSearchParams(query);
        [...params.entries()].forEach(([k, v]) => v === '' && params.delete(k));
        const search = params.toString();
        this.sectionEl?.classList.add('is-loading');
        try {
          const url = `${window.location.pathname}?${search}${search ? '&' : ''}section_id=${this.sectionId}`;
          const html = await (await fetch(url)).text();
          const fresh = parseHTML(html).querySelector('[data-collection-section]');
          if (fresh && this.sectionEl) {
            this.sectionEl.innerHTML = fresh.innerHTML;
          }
          history.pushState({ facets: true }, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
          if (scrollTop) this.sectionEl?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' });
        } catch (error) {
          window.location.search = search;
        } finally {
          this.sectionEl?.classList.remove('is-loading');
        }
      }
    }
  );

  window.addEventListener('popstate', (e) => {
    if (e.state?.facets) window.location.reload();
  });

  /* Async product lists (recommendations, complementary products) ---------- */
  class AsyncProducts extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.loaded) return;
      const load = async () => {
        this.dataset.loaded = 'true';
        try {
          const html = await (await fetch(this.dataset.url)).text();
          const fresh = parseHTML(html).querySelector(this.tagName.toLowerCase());
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
        } catch (e) {
          /* Recommendations are optional: fail silently. */
        }
      };
      new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      ).observe(this);
    }
  }
  define('product-recommendations', class extends AsyncProducts {});
  define('complete-the-look', class extends AsyncProducts {});

  define(
    'share-button',
    class extends HTMLElement {
      connectedCallback() {
        this.querySelector('button').addEventListener('click', async () => {
          const data = { url: this.dataset.url, title: this.dataset.title };
          if (navigator.share) {
            try {
              await navigator.share(data);
            } catch (e) {
              /* dismissed */
            }
            return;
          }
          await navigator.clipboard?.writeText(data.url);
          const note = this.querySelector('.share-button__copied');
          note.hidden = false;
          setTimeout(() => (note.hidden = true), 2500);
        });
      }
    }
  );

  /* Scroll reveal -------------------------------------------------------------- */
  const initReveal = (root = document) => {
    const els = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!document.body.classList.contains('has-reveal') || reducedMotion || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    // Se leen todas las posiciones primero y luego se escribe, para no forzar reflows.
    const vh = window.innerHeight;
    const above = Array.from(els, (el) => el.getBoundingClientRect().top < vh);
    els.forEach((el, i) => {
      if (above[i]) el.classList.add('is-revealed');
      else observer.observe(el);
    });
  };

  /* Customer pages ------------------------------------------------------------- */
  const initCustomer = () => {
    const recover = document.getElementById('Recover');
    const login = document.getElementById('Login');
    if (recover && login) {
      const show = (which) => {
        recover.hidden = which !== 'recover';
        login.hidden = which === 'recover';
      };
      if (window.location.hash === '#recover' || recover.querySelector('.form-message--error')) show('recover');
      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-customer-toggle]');
        if (t) {
          e.preventDefault();
          show(t.dataset.customerToggle);
        }
      });
    }
    document.querySelectorAll('form[data-confirm]').forEach((form) =>
      form.addEventListener('submit', (e) => {
        if (!window.confirm(form.dataset.confirm)) e.preventDefault();
      })
    );
    document.querySelectorAll('select[data-default]').forEach((select) => {
      if (select.dataset.default) select.value = select.dataset.default;
    });
  };

  /* Footer accordions start closed on mobile ------------------------------------ */
  const initFooter = () => {
    if (window.matchMedia('(max-width: 749px)').matches) {
      document.querySelectorAll('[data-footer-accordion]').forEach((d) => d.removeAttribute('open'));
    }
  };

  /* Consent: run non-essential code only after the visitor allows it --------------
   * Usage: theme.consent.whenAllowed('marketing', () => { ...load pixel... })
   * Categories: 'analytics', 'marketing', 'preferences', 'sale_of_data'.
   * Relies on Shopify's Customer Privacy API (enabled from Settings > Customer privacy).
   */
  theme.consent = {
    check(category) {
      const api = window.Shopify?.customerPrivacy;
      if (!api) return false;
      switch (category) {
        case 'analytics':
          return api.analyticsProcessingAllowed();
        case 'marketing':
          return api.marketingAllowed();
        case 'preferences':
          return api.preferencesProcessingAllowed();
        case 'sale_of_data':
          return api.saleOfDataAllowed();
        default:
          return false;
      }
    },
    whenAllowed(category, callback) {
      const run = () => {
        if (this.check(category)) {
          callback();
          return true;
        }
        return false;
      };
      const init = () => {
        if (run()) return;
        document.addEventListener('visitorConsentCollected', () => run(), { once: true });
      };
      if (window.Shopify?.customerPrivacy) return init();
      window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) init();
      });
    }
  };

  /* Theme editor support ------------------------------------------------------------ */
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const dialog = e.target.closest('dialog');
    if (dialog && !dialog.open) openDialog(dialog);
  });

  /* Init ------------------------------------------------------------------------------ */
  const init = () => {
    initReveal();
    initCustomer();
    initFooter();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
